<template>
  <Card class="mb-5">
    <CardContent class="expiration-filters">
      <div class="segmented-control" role="group" aria-label="Период">
        <Button
          v-for="preset in presets"
          :key="preset.value"
          size="sm"
          :variant="model.periodMode === preset.value ? 'secondary' : 'ghost'"
          :aria-pressed="model.periodMode === preset.value"
          @click="selectPreset(preset.value)"
        >
          {{ preset.label }}
        </Button>
      </div>
      <template v-if="model.periodMode === 'custom'">
        <div class="segmented-control" role="group" aria-label="Указать период">
          <Button
            size="sm"
            :variant="customKind === 'days' ? 'secondary' : 'ghost'"
            @click="customKind = 'days'"
          >
            Дни
          </Button>
          <Button
            size="sm"
            :variant="customKind === 'date' ? 'secondary' : 'ghost'"
            @click="customKind = 'date'"
          >
            Дата
          </Button>
        </div>
        <label v-if="customKind === 'days'" class="ui-field">
          <span class="ui-label">Дней</span>
          <Input
            :model-value="customDays"
            type="number"
            min="0"
            class="w-24"
            @update:model-value="emitCustomDays"
          />
        </label>
        <label v-else class="ui-field">
          <span class="ui-label">До</span>
          <Input
            :model-value="customUntil"
            type="date"
            :min="todayIso"
            @update:model-value="emitCustomDate"
          />
        </label>
      </template>
      <label class="ui-field">
        <span class="ui-label">Саппортер</span>
        <NativeSelect
          :model-value="model.supporterId"
          @update:model-value="emitPatch({ supporterId: $event ?? null })"
        >
          <option :value="null">Все саппортеры</option>
          <option v-for="item in supporterItems" :key="item.value" :value="item.value">
            {{ item.title }}
          </option>
        </NativeSelect>
      </label>
      <label class="ui-field">
        <span class="ui-label">Продукт</span>
        <NativeSelect
          :model-value="model.productId"
          @update:model-value="emitPatch({ productId: $event ?? null })"
        >
          <option :value="null">Все продукты</option>
          <option v-for="item in productItems" :key="item.value" :value="item.value">
            {{ item.title }}
          </option>
        </NativeSelect>
      </label>
      <label class="ui-field">
        <span class="ui-label">Тип</span>
        <NativeSelect :model-value="model.type" @update:model-value="emitPatch({ type: $event })">
          <option v-for="item in typeItems" :key="item.value" :value="item.value">
            {{ item.title }}
          </option>
        </NativeSelect>
      </label>
      <div class="segmented-control" role="group" aria-label="Ответственный">
        <Button
          size="sm"
          :variant="!model.mine ? 'secondary' : 'ghost'"
          :aria-pressed="!model.mine"
          @click="emitPatch({ mine: false })"
        >
          Все
        </Button>
        <Button
          size="sm"
          :variant="model.mine ? 'secondary' : 'ghost'"
          :aria-pressed="model.mine"
          @click="emitPatch({ mine: true })"
        >
          Мои
        </Button>
      </div>
      <label class="check-label">
        <Checkbox
          :model-value="model.hideOverdue"
          :disabled="model.periodMode === 'overdue'"
          @update:model-value="emitPatch({ hideOverdue: Boolean($event) })"
        />
        <span>Скрыть просроченные</span>
      </label>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { NativeSelect } from '@/components/ui/select';

import { computed, ref, watch } from 'vue';
import moment from 'moment';

export type PeriodMode = '7' | '30' | '90' | 'overdue' | 'custom';

export interface ExpirationsFilterModel {
  periodMode: PeriodMode;
  withinDays: number | null;
  untilDate: string | null;
  type: 'all' | 'support' | 'module';
  productId: number | null;
  supporterId: number | null;
  mine: boolean;
  hideOverdue: boolean;
}

interface Props {
  model: ExpirationsFilterModel;
  products: Product[];
  companies: Company[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:model': [value: ExpirationsFilterModel];
}>();

const presets = [
  { value: '7' as PeriodMode, label: 'Неделя' },
  { value: '30' as PeriodMode, label: 'Месяц' },
  { value: '90' as PeriodMode, label: 'Квартал' },
  { value: 'overdue' as PeriodMode, label: 'Просрочено' },
  { value: 'custom' as PeriodMode, label: 'Другое' },
];

const typeItems = [
  { title: 'Все типы', value: 'all' },
  { title: 'Поддержка', value: 'support' },
  { title: 'Лицензия', value: 'module' },
];

const todayIso = moment().format('YYYY-MM-DD');
const customKind = ref<'days' | 'date'>(props.model.untilDate ? 'date' : 'days');
const customDays = ref<number>(props.model.withinDays ?? 45);
const customUntil = ref<string>(props.model.untilDate || todayIso);

watch(
  () => props.model,
  (m) => {
    if (m.periodMode === 'custom') {
      if (m.untilDate) {
        customKind.value = 'date';
        customUntil.value = m.untilDate;
      } else if (m.withinDays != null) {
        customKind.value = 'days';
        customDays.value = m.withinDays;
      }
    }
  },
  { deep: true }
);

const productItems = computed(() => props.products.map((p) => ({ title: p.name, value: p.id })));
const supporterItems = computed(() => props.companies.map((c) => ({ title: c.name, value: c.id })));

function emitPatch(patch: Partial<ExpirationsFilterModel>) {
  emit('update:model', { ...props.model, ...patch });
}

function selectPreset(mode: PeriodMode) {
  if (mode === 'overdue') {
    emitPatch({
      periodMode: 'overdue',
      withinDays: null,
      untilDate: null,
      hideOverdue: false,
    });
    return;
  }
  if (mode === 'custom') {
    emitPatch({
      periodMode: 'custom',
      withinDays: customDays.value,
      untilDate: null,
    });
    customKind.value = 'days';
    return;
  }
  emitPatch({
    periodMode: mode,
    withinDays: Number(mode),
    untilDate: null,
  });
}

function emitCustomDays(value: number | string) {
  const days = Number(value);
  if (Number.isNaN(days) || days < 0) return;
  customDays.value = days;
  emitPatch({ periodMode: 'custom', withinDays: days, untilDate: null });
}

function emitCustomDate(value: string) {
  if (!value) return;
  customUntil.value = value;
  emitPatch({ periodMode: 'custom', withinDays: null, untilDate: value });
}

watch(customKind, (kind) => {
  if (props.model.periodMode !== 'custom') return;
  if (kind === 'days') emitCustomDays(customDays.value);
  else emitCustomDate(customUntil.value || todayIso);
});
</script>
